import React, { useContext } from 'react';
import { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';

import { Steps, Input, Form, Row, Col, Button, Divider, notification, Select, message, Skeleton, Grid, Popconfirm, Space, Card } from 'antd';
import { FileTextOutlined, EnvironmentOutlined, PhoneOutlined, MinusOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';

import { useMutation, useQuery } from '@tanstack/react-query';

import { EnumPessoaTipo, TypePessoa } from '../../../Models/Pessoa/Pessoa';
import { FormularioDeEnderecos } from '../../../Components/Formularios/FormularioDeEnderecos';
import { FornecedorService } from '../../../Services/Pessoa/FornecedorService';
import { PessoaService } from '../../../Services/Pessoa/PessoaService';
import { TypeFornecedor } from '../../../Models/Pessoa/Fornecedor';
import { FormInstance, useForm } from 'antd/es/form/Form';
import { FormularioDeContatos } from '../../../Components/Formularios/FormularioDeContatos';
import { TypeEndereco } from '../../../Models/Pessoa/Endereco';
import { TypeTelefone } from '../../../Models/Pessoa/Contato/Telefone';
import { TypeEmail } from '../../../Models/Pessoa/Contato/Email';
import { SelectComPesquisa } from '../../../Components/SelectComPesquisa';
import { PropriedadesDoPopConfirm } from '../../../Defaults';
import { DefaultOptionType } from 'antd/es/select';
import { PropriedadesDeNotificacao } from '../../../Utils/Notificacoes';

import { Permissoes } from '../../../Constants/Permissoes';
import { SessaoContext } from '../../../Contexts/SessaoContext';
import { Restrito } from '../../../Components/Restrito';

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import * as Tutoriais from "./fornecedor.tutorial"
import AdicionarAjuda, { ITutorial } from '../../../Components/AdicionarAjuda';
import { Formatacoes } from '../../../Utils/Formatacao';
import InputDate from '../../../Components/Base/Input/InputDate';
import DadosBancarioFormItemBasic from '@/Components/Populados/Pessoa/DadosBancario/DadosBancarioFormItemBasic';
import { dadosBancarioToString } from '@/Models/Pessoa/DadosBancario';
import InputCnpj from '@/Components/Populados/Pessoa/Documento/InputCnpj';

const fornecedorService = new FornecedorService();

type TipoDePessoa = "fisica" | "juridica"
const tipoDePessoa = (pessoa: TypePessoa | undefined): TipoDePessoa => pessoa?.cpf != undefined ? "fisica" : "juridica";

export const FormularioDeDadosBasicosDoFornecedor: React.FC<{
    form: FormInstance<TypeFornecedor>,
    dadosIniciais?: TypePessoa,
    depoisDeSubmeter?: (pessoa?: TypePessoa) => void,
}> = (props) => {
    const {
        setFieldsValue,
        setFieldValue
    } = props.form;

    const formWatch = Form.useWatch([], props.form);

    const { dadosIniciais } = props;

    const onFinish = (val: TypeFornecedor) => {
        const p: TypePessoa = {
            ...dadosIniciais,
            tipo: EnumPessoaTipo.FORNECEDOR,
            nome: val.nome,
            nomeSocial: val.nomeSocial,
            dataRegistro:
                val.dataRegistro &&
                (val.dataRegistro as unknown as Dayjs).format("YYYY-MM-DD"),
            inscricaoEstadual: val.inscricaoEstadual,
            inscricaoMunicipal: val.inscricaoMunicipal,
            segmentoAtuacao: {
                codigo: Number(val.segmentoAtuacao),
            },
            materialServicoPrestado: {
                codigo: Number(val.materialServicoPrestado),
            },
            descricaoMaterialServicoPrestado: val.descricaoMaterialServicoPrestado,
            site: val.site,
            enderecos: (val.enderecos ?? []).length === 0 ? undefined : (val.enderecos ?? []),
            telefones: (val.telefones ?? []).length === 0 ? undefined : (val.telefones ?? []),
            dadosBancarios: val.dadosBancarios,
            ...(() =>
                pessoaFisicaOuJuridica == "juridica"
                    ? { cnpj: Formatacoes.sanitizar("_-/.", val.cnpj)?.slice(0, 14), cpf: undefined }
                    : { cpf: Formatacoes.sanitizar("_-/.", val.cpf)?.slice(0, 11), cnpj: undefined }
            )(),
        };

        if (props.depoisDeSubmeter) props.depoisDeSubmeter(p);
    }

    const segmentosDeAtuacaoDisponiveis = useQuery({
        queryKey: ["listarSegmentosDeAtuacao"],
        queryFn: () => new PessoaService().listarSegmentosDeAtuacao(EnumPessoaTipo.FORNECEDOR)
    })

    const materiaisServicos = useQuery({
        queryKey: ["listarMateriaisServicos"],
        queryFn: () => new PessoaService().listarMateriaisServicos()
    })

    const [pessoaFisicaOuJuridica, setPessoaFisicaOuJuridica] = useState<TipoDePessoa>(tipoDePessoa(props.dadosIniciais));

    return (
        <React.Fragment>
            <Form<TypeFornecedor>
                form={props.form}
                onFinish={onFinish}
                autoComplete="off"
                layout="vertical"
            >
                <React.Fragment>
                    <Form.Item<TypeFornecedor> initialValue={dadosIniciais?.enderecos} name="enderecos" hidden>
                    </Form.Item>
                    <Form.Item<TypeFornecedor> initialValue={dadosIniciais?.telefones} name="telefones" hidden>
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item<TypeFornecedor>
                                name="nome"
                                rules={[
                                    {
                                        required: true,
                                        message: "Por favor, informe a razão social!",
                                    },
                                ]}
                                initialValue={dadosIniciais?.nome}
                                label="Razão Social"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item<TypeFornecedor>
                                name="nomeSocial"
                                rules={[
                                    {
                                        required: true,
                                        message: "Por favor, informe o nome fantasia!",
                                    },
                                ]}
                                initialValue={dadosIniciais?.nomeSocial}
                                label="Nome Fantasia"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} md={6}>
                            <Space.Compact block>
                                <Form.Item label="Pessoa" style={{ flex: 1 }}>
                                    <Select
                                        onChange={(v) => setPessoaFisicaOuJuridica(v)}
                                        value={pessoaFisicaOuJuridica}
                                        options={[
                                            {
                                                label: "Física",
                                                value: "fisica",
                                            },
                                            {
                                                label: "Juridica",
                                                value: "juridica",
                                            },
                                        ]}
                                    />
                                </Form.Item>
                                {pessoaFisicaOuJuridica == "juridica" ? (
                                    <Form.Item<TypeFornecedor>
                                        style={{ flex: 2 }}
                                        name="cnpj"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Por favor, informe o CNPJ!",
                                            },
                                        ]}
                                        initialValue={dadosIniciais?.cnpj}
                                        label="CNPJ"
                                    >
                                        <InputCnpj
                                            initialValue={dadosIniciais?.cnpj?.trim?.() ?? ''}
                                            onChangePessoa={(pessoa) => {
                                                setFieldsValue(pessoa);
                                            }}
                                            onErroAoBuscarDados={() => {
                                            }}
                                        />
                                    </Form.Item>
                                ) : (
                                    <Form.Item<TypeFornecedor>
                                        style={{ flex: 2 }}
                                        name="cpf"
                                        rules={[
                                            { required: true, message: "Por favor, informe o CPF" },
                                        ]}
                                        initialValue={dadosIniciais?.cpf}
                                        label="CPF"
                                    >
                                        <InputMask mask="999.999.999-99">
                                            <Input />
                                        </InputMask>
                                    </Form.Item>
                                )}
                            </Space.Compact>
                        </Col>

                        <Col xs={24} md={6}>
                            <Form.Item<TypeFornecedor>
                                name="dataRegistro"
                                label="Data de Registro"
                                initialValue={
                                    dadosIniciais?.dataRegistro &&
                                    dayjs(dadosIniciais!.dataRegistro!, "YYYY-MM-DD").isValid()
                                    ? dayjs(dadosIniciais!.dataRegistro) : undefined
                                }
                                // getValueProps={(value) => {
                                //     return value;
                                // }}
                            >
                                <InputDate style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item<TypeFornecedor>
                                name="inscricaoEstadual"
                                initialValue={dadosIniciais?.inscricaoEstadual}
                                label="Inscrição Estadual"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item<TypeFornecedor>
                                name="inscricaoMunicipal"
                                initialValue={dadosIniciais?.inscricaoMunicipal}
                                label="Inscrição Municipal"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </React.Fragment>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item<TypeFornecedor>
                            name="segmentoAtuacao"
                            initialValue={dadosIniciais?.segmentoAtuacao?.codigo}
                            label="Segmento de Atuação"
                        >
                            <SelectComPesquisa
                                loading={
                                    segmentosDeAtuacaoDisponiveis.isLoading ||
                                    segmentosDeAtuacaoDisponiveis.isFetching
                                }
                                disabled={segmentosDeAtuacaoDisponiveis.isError}
                                options={segmentosDeAtuacaoDisponiveis.data?.data.map(
                                    (item) => {
                                        return {
                                            value: item.codigo,
                                            label: item.descricao,
                                        };
                                    }
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item<TypeFornecedor>
                            name="site"
                            initialValue={dadosIniciais?.site}
                            label="Site"
                        >
                            <Input addonBefore="https://" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item<TypeFornecedor>
                            name="descricaoMaterialServicoPrestado"
                            initialValue={dadosIniciais?.descricaoMaterialServicoPrestado}
                            label="Descrição dos materiais ou serviços prestados"
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item<TypeFornecedor>
                            name="materialServicoPrestado"
                            initialValue={dadosIniciais?.materialServicoPrestado?.codigo}
                            label="Tipo de material ou serviço prestado"
                        >
                            <SelectComPesquisa
                                loading={
                                    materiaisServicos.isLoading || materiaisServicos.isFetching
                                }
                                disabled={materiaisServicos.isError}
                                options={materiaisServicos.data?.data.map((item) => {
                                    return {
                                        value: item.codigo,
                                        label: item.descricao,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.List name={["dadosBancarios"]} initialValue={dadosIniciais?.dadosBancarios}>
                    {(fields, { add, remove }, { errors, warnings }) => (
                        <>
                            <Divider orientation="left" style={{ marginTop: 0 }}>
                                Dados bancários
                            </Divider>
                            <Button
                                type="dashed"
                                onClick={() => add({})}
                                icon={<PlusOutlined />}
                                style={{ marginBottom: 10 }}
                            >
                                Adicionar rateio
                            </Button>
                            <Form.ErrorList warnings={warnings} errors={errors}></Form.ErrorList>
                            {/* {fields?.map(({ name, key }) => (
                            <Col xs={24}>
                                <Card size="small" extra={[
                                    <MinusCircleOutlined onClick={() => remove(name)} />
                                ]}>
                                    <Card.Meta 
                                        description={
                                            <Form.Item name={name} style={{ marginBottom : 0 }}>
                                                <DadosBancarioFormItemBasic />
                                            </Form.Item>
                                        }
                                    />
                                </Card>
                            </Col>
                        ))} */}
                            {fields?.map(({ name, key }) => (
                                <Row gutter={8} wrap={true} align={"middle"} style={{ width: "100%" }}>
                                    <Col xs={3} lg={2}>
                                        <Button type="link" onClick={() => remove(name)}>
                                            <MinusCircleOutlined /> Remover
                                        </Button>
                                    </Col>
                                    <Col xs={21} lg={22}>
                                        <Form.Item name={name} style={{ marginBottom: 0 }}>
                                            <DadosBancarioFormItemBasic />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Divider></Divider>
                                    </Col>
                                </Row>
                            ))}
                        </>
                    )}
                </Form.List>
            </Form>
        </React.Fragment>
    );
}

const ListagemDeFornecedores: React.FC<{
    aoAdicionar?: () => any,
    aoEditar?: (fornecedor: TypeFornecedor) => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (fornecedor: TypeFornecedor) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const [filtro, setFiltro] = useState<TypeFornecedor>({ tipo: EnumPessoaTipo.FORNECEDOR });

    interface IFormFiltro {
        txtRazaoSocial: string,
        txtNomeFantasia: string,
        txtCnpj: string,
        txtCpf: string,
        ddlAtivo: "1" | "0" | "null",
    }

    const listaFornecedores = useQuery({
        queryKey: ['listar', filtro],
        queryFn: () => fornecedorService.listar(filtro),
        enabled: true,
        refetchOnWindowFocus: false
    });

    const onFinish = (values: IFormFiltro) => {
        const _filtro: TypeFornecedor = {
            nome: values.txtRazaoSocial,
            nomeSocial: values.txtNomeFantasia,
            cnpj: values.txtCnpj && Formatacoes.sanitizar("_-/.", values.txtCnpj),
            cpf: values.txtCpf && Formatacoes.sanitizar("_-/.", values.txtCpf),
            ativo: values.ddlAtivo !== "null" ? values.ddlAtivo === '1' : undefined,
            tipo: EnumPessoaTipo.FORNECEDOR
        }

        setFiltro(_filtro);
    }

    useEffect(() => {
        listaFornecedores.refetch();
    }, [filtro]);

    const adicionar = !!permissoes?.includes(Permissoes.CriarFornecedor);
    const [chavesDasLinhasExpandidas, setChavesDasLinhasExpandidas] = useState<React.Key[]>([])

    return (
        <>
            <Form<IFormFiltro> name="formListaFornecedores" onFinish={onFinish} layout="vertical" autoComplete="off">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro}>
                    <Col xs={24} xl={6} id={Tutoriais.IDs.filtroDeRazaoSocial}>
                        <Form.Item<IFormFiltro> name="txtRazaoSocial" label="Razão Social">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6} id={Tutoriais.IDs.filtroDeNomeFantasia}>
                        <Form.Item<IFormFiltro> name="txtNomeFantasia" label="Nome Fantasia">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={5} id={Tutoriais.IDs.filtroDeCNPJ}>
                        <Form.Item<IFormFiltro> name="txtCnpj" label="CNPJ">
                            <InputMask mask="99.999.999/9999-99">
                                <Input placeholder="Todos" />
                            </InputMask>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={4}>
                        <Form.Item<IFormFiltro> name="txtCpf" label="CPF">
                            <InputMask mask="999.999.999-99">
                                <Input placeholder="Todos" />
                            </InputMask>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFormFiltro> name="ddlAtivo" label="Ativo" initialValue={"1"}>
                            <Select
                                options={(() => {
                                    const opcoes: (DefaultOptionType & {
                                        value: IFormFiltro["ddlAtivo"];
                                    })[] = [
                                            { value: "null", label: "Todos" },
                                            { value: "1", label: "Ativo" },
                                            { value: "0", label: "Inativo" },
                                        ];
                                    return opcoes;
                                })()}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={adicionar ? 12 : 24} xl={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex" }}>
                        <Form.Item style={{ width: "100%" }}>
                            <Button
                                type="primary"
                                id={Tutoriais.IDs.botaoListar}
                                htmlType="submit"
                                disabled={listaFornecedores.isFetching}
                                style={{ width: "100%" }}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionar}>
                        <Col xs={12} xl={3} style={{ alignItems: "end", display: "flex" }}>
                            <Form.Item style={{ width: "100%" }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeFornecedor>
                        bordered
                        locale={{ emptyText: "Nenhum fornecedor encontrado." }}
                        dataSource={listaFornecedores.data?.data}
                        loading={listaFornecedores.isFetching}
                        rowKey={(k) => k.codigo!}
                        addTitleOn="lg"
                        expandable={{
                            showExpandColumn: false,
                            expandedRowRender: (record) => <div style={{ overflowWrap: "anywhere" }}>{JSON.stringify(record)}</div>,
                            rowExpandable: () => true,
                            expandedRowKeys: chavesDasLinhasExpandidas,
                        }}
                        columns={[
                            {
                                title: "Código",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                dataIndex: "codigo",
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                key: "ativo",
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v: boolean) => {
                                    return <>{v ? "Sim" : "Não"}</>;
                                },
                            },
                            {
                                title: "Razão social",
                                breakpoints: { xs: 8, sm: 8, lg: 6 },
                                dataIndex: ["nome"],
                                sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                                key: "nome",
                            },
                            {
                                title: "Nome fantasia",
                                breakpoints: { xs: 8, sm: 8, lg: 5 },
                                dataIndex: ["nomeSocial"],
                                sorter: (a, b) => Ordenar.String(a.nomeSocial, b.nomeSocial),
                                key: "nome-social",
                            },
                            {
                                title: "CNPJ ou CPF",
                                breakpoints: { xs: 8, sm: 8, lg: 4 },
                                csv: {
                                    title: "CNPJ/CPF",
                                    get: (x) => (
                                        x == undefined ? "" :
                                            (tipoDePessoa(x) == "juridica" ? Formatacoes.formatarCNPJ(x.cnpj) : Formatacoes.formatarCPF(x.cpf)) ?? ""
                                    ),
                                },
                                sorter: (a, b) => Ordenar.String(a.cnpj ?? a.cpf, b.cnpj ?? b.cpf),
                                render: (val, record) =>
                                    tipoDePessoa(record) == "juridica" ? Formatacoes.formatarCNPJ(record.cnpj) : Formatacoes.formatarCPF(record.cpf),
                                key: "cnpj-cpf",
                            },

                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 5 },
                                hidden: [Permissoes.AtualizarFornecedor, Permissoes.DesativarFornecedor, Permissoes.AtivarFornecedor].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{
                                                justifyContent: tamanhoDaTela.sm ? "unset" : "center",
                                                display: "flex",
                                                flexWrap: "wrap",
                                            }}
                                            itens={[
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={item.codigo && chavesDasLinhasExpandidas.includes(item.codigo) ? <MinusOutlined /> : <PlusOutlined />}
                                                    onClick={() => {
                                                        if (!item.codigo) return;

                                                        if (chavesDasLinhasExpandidas.includes(item.codigo)) {
                                                            setChavesDasLinhasExpandidas((prev) => prev.filter((v) => v != item.codigo));
                                                        } else {
                                                            setChavesDasLinhasExpandidas((prev) => [...prev, item.codigo!]);
                                                        }
                                                    }}
                                                ></Button>,
                                                ...(() => {
                                                    if (item.ativo) {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtualizarFornecedor) && (
                                                                <Link
                                                                    to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                    onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                                >
                                                                    <Button
                                                                        className={Tutoriais.Classes.botaoEditar}
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            props.aoEditar?.(item);
                                                                        }}
                                                                    >
                                                                        Editar
                                                                    </Button>
                                                                </Link>
                                                            ),

                                                            permissoes?.includes(Permissoes.DesativarFornecedor) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("desativar")}
                                                                    onConfirm={() => {
                                                                        item.codigo &&
                                                                            fornecedorService.desativar(item.codigo).then(() => {
                                                                                listaFornecedores.refetch();
                                                                            });
                                                                    }}
                                                                >
                                                                    <Button type="default" danger size="small" id={Tutoriais.Classes.botaoDesativar}>
                                                                        Desativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    } else {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtivarFornecedor) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("ativar")}
                                                                    onConfirm={() => {
                                                                        item.codigo &&
                                                                            fornecedorService.ativar(item.codigo).then(() => {
                                                                                listaFornecedores.refetch();
                                                                            });
                                                                    }}
                                                                >
                                                                    <Button type="dashed" size="small">
                                                                        Reativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    }
                                                })(),
                                            ]}
                                        />
                                    );
                                },
                            },
                        ]}
                        csv={{
                            enabled: true,
                            filename: "fornecedores.csv",
                            concat(dados) {
                                return [
                                    {
                                        header: "Dados bancários",
                                        data: dados.map(m => {
                                            if ((m.dadosBancarios?.length ?? 0) == 0) return ' - ';

                                            return m.dadosBancarios?.map(mb => dadosBancarioToString(mb)).join(' | ');
                                        })
                                    }
                                ]
                            }
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda
                tutoriais={
                    [
                        {
                            titulo: "Como consultar fornecedores",
                            tutorial: Tutoriais.ConsultarFornecedor,
                        },

                        permissoes?.includes(Permissoes.CriarFornecedor) && {
                            titulo: "Como cadastrar um novo fornecedor",
                            tutorial: Tutoriais.AdicionarFornecedor,
                        },

                        permissoes?.includes(Permissoes.AtualizarFornecedor) && {
                            titulo: "Como editar um fornecedor",
                            tutorial: Tutoriais.EditarFornecedor,
                        },

                        permissoes?.includes(Permissoes.DesativarFornecedor) && {
                            titulo: "Como desativar um fornecedor",
                            tutorial: Tutoriais.DesativarFornecedor,
                        },
                    ].filter((v) => v != false) as ITutorial[]
                }
            />
        </>
    );
}

const CadastroDeFornecedor: React.FC<{
    fornecedorEmAlteracao?: TypeFornecedor,

    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any,
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const [passoAtual, setPassoAtual] = useState(0);

    const [fornecedor, setFornecedor] = useState<TypeFornecedor>(props.fornecedorEmAlteracao ?? {});

    const [enderecos, setEnderecos] = useState<TypeEndereco[]>(props.fornecedorEmAlteracao?.enderecos ?? []);
    const [telefones, setTelefones] = useState<TypeTelefone[]>(props.fornecedorEmAlteracao?.telefones ?? []);
    const [emails, setEmails] = useState<TypeEmail[]>(props.fornecedorEmAlteracao?.emails ?? []);

    useEffect(() => {
        setFornecedor((prev) => ({ ...prev, emails: emails }))
    }, [emails])

    useEffect(() => {
        setFornecedor((prev) => ({ ...prev, enderecos: enderecos }))
    }, [enderecos])

    useEffect(() => {
        setFornecedor((prev) => ({ ...prev, telefones: telefones }))
    }, [telefones])

    const inserePessoa = useMutation({
        mutationFn: (fornecedorParam: TypePessoa) => fornecedorService.inserir(fornecedorParam),
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })

            // setPainelAtual('listagem');
            props.depoisDeFinalizar?.();
        },
    })

    const atualizaPessoa = useMutation({
        mutationFn: (fornecedorParam: TypePessoa) => fornecedorService.atualizar(fornecedorParam),
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Alteração realizada com sucesso.')
            })

            // setPainelAtual('listagem');
            props.depoisDeFinalizar?.();
        },
    })

    const proximoPasso = () => {
        const passo = passoAtual + 1

        if (passo < passos.length) {
            setPassoAtual(passo)
        }
    }

    const passoAnterior = () => {
        const passo = (passoAtual - 1)
        if (passo >= 0) {
            setPassoAtual(passo)
        } else {
            props.aoCancelar?.();
        }
    }

    const [formDadosBasicos] = useForm()

    const passos = [
        {
            title: 'Dados Básicos',
            icon: <FileTextOutlined />,
            conteudo: <React.Fragment>
                <FormularioDeDadosBasicosDoFornecedor
                    form={formDadosBasicos}
                    depoisDeSubmeter={(p) => {
                        setFornecedor((prev) => ({ ...prev, ...p }));

                        if ((p?.enderecos?.length ?? 0) > 0) setEnderecos(p?.enderecos!);
                        if ((p?.telefones?.length ?? 0) > 0) setTelefones(p?.telefones!);

                        proximoPasso();
                    }}
                    dadosIniciais={fornecedor}
                />
                <Row>
                    <Col xs={24}>
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Button danger type="primary" style={{ margin: '0 8px' }} onClick={() => passoAnterior()}>
                                Cancelar
                            </Button>
                            <Button type="primary" onClick={() => {
                                formDadosBasicos.submit();
                            }}>
                                Avançar
                            </Button>
                        </div>
                    </Col>
                </Row>
            </React.Fragment>,
            style: {
                fontWeight: 600
            }
        },
        {
            title: 'Endereços',
            icon: <EnvironmentOutlined />,
            conteudo: <React.Fragment>
                <FormularioDeEnderecos permitirEdicaoDosEnderecos enderecosAPI={[enderecos, setEnderecos]} distancia={false} />
                <Row>
                    <Col xs={24}>
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Button danger type="primary" style={{ margin: '0 8px' }} onClick={() => passoAnterior()}>
                                Voltar
                            </Button>
                            <Button type='primary' onClick={() => {
                                if (enderecos.filter(f => f.ativo !== false).length > 0) {
                                    setFornecedor({
                                        ...fornecedor!,
                                        enderecos: enderecos
                                    })
                                }
                                // else {
                                //     message.destroy();
                                //     message.open({
                                //         type: 'warning',
                                //         content: 'Adicione ao menos um endereço antes de avançar.'
                                //     })
                                // }

                                proximoPasso();
                            }
                            }>
                                Avançar
                            </Button>
                        </div>
                    </Col>
                </Row>
            </React.Fragment>,
            style: {
                fontWeight: 600
            }
        },
        {
            title: 'Contatos',
            icon: <PhoneOutlined />,
            conteudo: <React.Fragment>
                <FormularioDeContatos telefonesAPI={[telefones, setTelefones]} emailsAPI={[emails, setEmails]}
                />
                <Row>
                    <Col xs={24}>
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Button danger type="primary" style={{ margin: '0 8px' }} onClick={() => passoAnterior()}>
                                Voltar
                            </Button>
                            <Button type="primary"
                                disabled={!permissoes?.includes(props.fornecedorEmAlteracao != undefined ? Permissoes.AtualizarFornecedor : Permissoes.CriarFornecedor)}
                                onClick={() => {
                                    message.destroy();
                                    // if (!fornecedor?.telefones || fornecedor.telefones.length == 0)
                                    //     message.open({
                                    //         type: 'warning',
                                    //         content: 'Adicione ao menos um telefone antes de salvar.'
                                    //     })
                                    // else if (!fornecedor?.emails || fornecedor.emails.length == 0)
                                    //     message.open({
                                    //         type: 'warning',
                                    //         content: 'Adicione ao menos um endereço de e-mail antes de salvar.'
                                    //     })
                                    // else {

                                    /*
                                     No backend, o tipo de alteração a ser realizada é identificado pelo codigo do item.
                                    - Se o codigo for 0, aplicas-e uma modificação.
                                    - Se for maior que 0, aplica-se uma ativação/desativação  
                                    */

                                    if (fornecedor.codigo === undefined) {
                                        inserePessoa.mutate(
                                            {
                                                ...fornecedor,
                                                enderecos: fornecedor.enderecos?.map((e) => ({ ...e, codigo: undefined })),
                                                emails: fornecedor.emails?.map((e) => ({ ...e, codigo: undefined })),
                                                telefones: fornecedor.telefones?.map((t) => ({ ...t, codigo: undefined }))
                                            }
                                        )
                                    }
                                    else {
                                        atualizaPessoa.mutate({
                                            ...fornecedor,
                                            enderecos: fornecedor.enderecos?.map((e) => e.codigo && e.codigo < 0 ? ({ ...e, codigo: undefined }) : e),
                                            emails: fornecedor.emails?.map((em) => em.codigo && em.codigo < 0 ? ({ ...em, codigo: undefined }) : em),
                                            telefones: fornecedor.telefones?.map((tel) => tel.codigo && tel.codigo < 0 ? ({ ...tel, codigo: undefined }) : tel)
                                        });
                                    }

                                    // }
                                }
                                }>
                                Salvar
                            </Button>
                        </div>
                    </Col>
                </Row>
            </React.Fragment>,
            style: {
                fontWeight: 600
            }
        },
    ]

    return (
        <>
            <Steps
                current={passoAtual}
                items={passos.map((m) => ({
                    title: m.title,
                    icon: m.icon,
                    style: m.style
                }))}
            />
            <div style={{ marginTop: 15 }}>
                {passos[passoAtual].conteudo}
            </div>
        </>
    )
}

export const Fornecedor: React.FC<{}> = (props) => {
    const [fornecedorEmAlteracao, setFornecedorEmAlteracao] = useState<TypeFornecedor | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterFornecedor = useMutation({
        mutationFn: (id: number) => new FornecedorService().listar({ codigo: id, ativo: true }),
        gcTime: 0,
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                const fornecedor = resposta.data.at(0);
                if (fornecedor?.dataRegistro)
                    fornecedor.dataRegistro = dayjs(fornecedor?.dataRegistro);
                setFornecedorEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarFornecedores, Permissoes.CriarFornecedor, Permissoes.AtualizarFornecedor, Permissoes.DesativarFornecedor])
    useWatchParameterOnRoutes({
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))],
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterFornecedor.mutate(Number(param)); },
    })

    return (
        <>
            <h2 style={{ marginTop: 0 }}>
                {
                    rotaDeVisualizacao ? "Detalhes da Fornecedor" : rotaDeEdicao ? "Edição da Fornecedor" : "Cadastro de Fornecedor"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarFornecedores) &&
                    <Route path="*" index element={
                        <ListagemDeFornecedores
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(fornecedor) => {
                                setFornecedorEmAlteracao(fornecedor);
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarFornecedor) &&
                    <Route path="/cadastro" element={
                        <CadastroDeFornecedor
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar={() => { navigate(raiz) }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarFornecedor) &&
                    <Route path="/editar" element={
                        fornecedorEmAlteracao == undefined ? <Skeleton active /> :
                            <CadastroDeFornecedor
                                fornecedorEmAlteracao={fornecedorEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                    } />
                }
            </Routes>

        </>
    );
}